'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
    prompt(): Promise<void>;
}

interface InstallContextType {
    deferredPrompt: BeforeInstallPromptEvent | null;
    handleInstallClick: () => Promise<void>;
}

const InstallContext = createContext<InstallContextType | undefined>(undefined);

export const InstallProvider = ({ children }: { children: ReactNode }) => {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

    useEffect(() => {
        const handler = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
        };

        window.addEventListener('beforeinstallprompt', handler);

        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;

        await deferredPrompt.prompt();

        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User response: ${outcome}`);

        setDeferredPrompt(null);
    };

    return (
        <InstallContext.Provider value={{ deferredPrompt, handleInstallClick }}>
            {children}
        </InstallContext.Provider>
    );
};

export const useInstall = () => {
    const context = useContext(InstallContext);
    if (!context) throw new Error("useInstall must be used within an InstallProvider");
    return context;
};
